import { DEFAULT_WALLPAPER, NO_WALLPAPER } from '@omnia/shared';

export type WallpaperTone = 'dark' | 'light';

export interface Wallpaper {
  id: string;
  label: string;
  /** Brightness of the picture, which decides the legibility scrim and the text colour drawn straight on it. */
  tone: WallpaperTone;
  /** Native width of the full-size file, for the `srcset` descriptor. */
  width: number;
  /** Native height, so the layer reserves its box before the file arrives. */
  height: number;
  full: string;
  small: string;
  thumb: string;
}

/** Vite fingerprints these and nothing is fetched until an `<img>` points at one. */
const files = import.meta.glob('./*.webp', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

const url = (name: string) => files[`./${name}.webp`] ?? '';

/** Order is the order of the picker; sizes are the source images' native pixels. */
const SOURCES: readonly { id: string; label: string; tone: WallpaperTone; width: number; height: number }[] = [
  { id: 'halka', label: 'Halka', tone: 'dark', width: 1617, height: 973 },
  { id: 'halka-acik', label: 'Halka (Açık)', tone: 'light', width: 1616, height: 973 },
  { id: 'dalga', label: 'Dalga', tone: 'dark', width: 1616, height: 973 },
  { id: 'dalga-acik', label: 'Dalga (Açık)', tone: 'light', width: 1617, height: 973 },
  { id: 'sehir-gece', label: 'Şehir Gecesi', tone: 'dark', width: 1616, height: 973 },
  { id: 'sehir-gun', label: 'Şehir Günbatımı', tone: 'light', width: 1616, height: 973 },
  { id: 'tabela', label: 'Tabela', tone: 'dark', width: 1616, height: 973 },
  { id: 'tabela-acik', label: 'Tabela (Açık)', tone: 'light', width: 1616, height: 973 },
  { id: 'kavis', label: 'Kavis', tone: 'dark', width: 1672, height: 941 },
  { id: 'kavis-acik', label: 'Kavis (Açık)', tone: 'light', width: 1672, height: 941 },
  { id: 'siyah', label: 'Siyah', tone: 'dark', width: 1672, height: 941 },
  { id: 'beyaz', label: 'Beyaz', tone: 'light', width: 1672, height: 941 },
  { id: 'cizgi', label: 'Çizgi', tone: 'dark', width: 1672, height: 941 },
  { id: 'cizgi-acik', label: 'Çizgi (Açık)', tone: 'light', width: 1672, height: 941 },
];

export const WALLPAPERS: readonly Wallpaper[] = SOURCES.map((source) => ({
  ...source,
  full: url(source.id),
  small: url(`${source.id}-s`),
  thumb: url(`${source.id}-t`),
}));

export { DEFAULT_WALLPAPER, NO_WALLPAPER };

export const findWallpaper = (id: string): Wallpaper | undefined => WALLPAPERS.find((wallpaper) => wallpaper.id === id);

/** An id that no longer exists (or `none`) resolves to what the user would see by default, except `none` itself stays plain. */
export function resolveWallpaperId(id: string | undefined): string {
  if (id === NO_WALLPAPER) return NO_WALLPAPER;
  return id !== undefined && findWallpaper(id) ? id : DEFAULT_WALLPAPER;
}
